<script>
  import { onMount, getContext } from "svelte";
  import { navigate } from "svelte-routing";
  import isEmpty from "lodash/isEmpty";
  import { LinearDisplayFormat } from "dimtext";
  import get from "lodash/get";
  import { distSq } from "vector";

  import ItemBadges from "./ItemBadges.svelte";
  import liteDrawing from "@local/extensions/drawing/lite-drawing.js";
  import { draggable } from "svelte-utilities";
  import {
    selectedItems,
    selectedCollections,
    selectedType,
    settingDisplayTnQty,
    settingDisplayTnDims,
    settingDisplayTnMark,
    settingDisplayTnBadges,
    settingDisplayEdgeOffsets,
    pathRoot,
  } from "#src/stores/ui.js";

  import imageUrl from "#src/extensions/image-url.js";
  import imageDecode from "#src/extensions/dom/image-decode.js";
  import { discontiguousKey, modifierKey } from "#src/extensions/event-keys.js";
  import { drawingSrc as getDrawingSrc } from "#src/api";
  import { multiplier } from "@local/extensions/utilities/scales.js";

  export let group;
  export let item;
  export let itemList;
  export let maxHeight;
  export let cssHeight;
  export let dragging;
  export let isDragging;
  export let category = null;
  export let displayUnit;
  export let displayPrecision;
  export let dimFormat;
  export let isCollection = false;
  export let comments = 0;
  export let attachments = 0;
  export let collectionItems = 0;

  const typeColors = getContext("typeColorsTrue");
  const dpi = window.devicePixelRatio;

  // Padding around each thumbnail, in px
  const top = 4;
  const right = 4;
  const left = 4;

  // Size of thumbnail for uploaded drawing (unscaled)
  const ts = 60;

  let container;
  let canvas;
  let ctx;
  let dragStartPt;
  let imageProps;

  $: maxWidth = maxHeight * 10;
  $: maxAspectRatio = maxWidth / maxHeight;
  $: typeColor = $typeColors[item.type_id];
  $: currentType = $group.types[item.type_id];
  $: dimstring = getDimString(item, dimFormat, displayPrecision, currentType);
  $: bottom = ($settingDisplayTnDims ? 19 : 0) + ($settingDisplayTnMark ? 30 : 0) + 1;
  $: maxH = cssHeight - top - bottom;
  $: selectionSet = isCollection ? selectedCollections : selectedItems;
  $: selected = $selectionSet[item.id];
  $: replaceWithImage =
    (item.drawing && item.replace_with_drawing) || (item.shape.type === "none" && currentType?.image);
  $: scaledDrawingSize = getScaledDrawingSize(item);
  $: thumbnailHt = getThumbnailHt(
    item,
    cssHeight,
    maxHeight,
    replaceWithImage,
    imageProps,
    scaledDrawingSize,
  );
  $: offsetY = getOffsetY(ctx, thumbnailHt);
  $: containerHt = getContainerHt(item, cssHeight, offsetY, scaledDrawingSize);
  $: renderProps =
    ctx && getRenderProps(item, containerHt, maxAspectRatio, thumbnailHt, imageProps, scaledDrawingSize);
  $: offsetX = getOffsetX(ctx, offsetY, renderProps);
  $: thumbnailWd = getThumbnailWd(
    thumbnailHt,
    offsetX,
    maxHeight,
    maxAspectRatio,
    item,
    replaceWithImage,
    imageProps,
    scaledDrawingSize,
  );
  $: leftOffset = getLeftOffset(renderProps, thumbnailWd, offsetX);
  $: drawingSrc = getImage(item, currentType);
  $: drawing = liteDrawing(item, {
    offsetX,
    offsetY,
    typeColor,
    labelBy: isCollection ? $group.data.settings.collections_label_by : $group.data.settings.label_by,
    showDims: $settingDisplayTnDims,
    showMark: $settingDisplayTnMark,
    showQty: $settingDisplayTnQty,
    showOffsets: $settingDisplayEdgeOffsets,
    isCollection,
    imageProps,
  });
  $: refresh(drawingSrc);
  $: {
    if (ctx && renderProps) {
      item;
      cssHeight;
      selected;
      render(renderProps);
    }
  }

  function getImage(item, currentType) {
    if (item.drawing && item.replace_with_drawing) {
      return getDrawingSrc(item.drawing);
    } else if (item.shape.type === "none" && currentType?.image) {
      return imageUrl(currentType.image);
    }
  }

  async function refresh(drawingSrc) {
    if (item.shape.type === "none" && currentType?.image) {
      const p = await imageDecode(drawingSrc);
      imageProps = p;
    }
  }

  function getDimString(item, dimFormat, displayPrecision, currentType) {
    if (item.shape.type === "none") {
      return currentType?.name;
    } else {
      const fmt = {
        DECIMAL: LinearDisplayFormat.DECIMAL,
        FRACTIONAL: LinearDisplayFormat.FRACTIONAL,
        ARCHITECTURAL: LinearDisplayFormat.ARCHITECTURAL,
      }[dimFormat];
      if (replaceWithImage) return "";
      const widthTxt = item.cache.width_prime?.format(fmt, displayPrecision, {
        displayUnit,
        unitSymbols: "ascii",
        unicodeNumerals: true,
        unicodeSlash: true,
        fractionSeparator: false,
      });

      const heightText = item.cache.height_prime?.format(fmt, displayPrecision, {
        displayUnit,
        unitSymbols: "ascii",
        unicodeNumerals: true,
        unicodeSlash: true,
        fractionSeparator: false,
      });

      return `${widthTxt} x ${heightText}`;
    }
  }

  function getRenderProps(item, containerHt, maxAspectRatio, thumbnailHt, imageProps, scaledDrawingSize) {
    const h = Math.ceil(containerHt * dpi); // drawing height in physical px
    const tH = thumbnailHt * dpi; // thumbnail height in physical px
    let width;
    let zs; // zoom scale
    if (item.drawing && item.replace_with_drawing) {
      if (item.drawing.properties) {
        const ar = item.drawing.properties.width / item.drawing.properties.height;
        if (scaledDrawingSize) {
          const wpct = Math.min(ar, maxAspectRatio);
          width = scaledDrawingSize.height * wpct;
          const pct = item.cache.height / maxHeight;
          const iH = Math.floor(maxH * pct) * dpi;
          zs = iH / item.cache.height;
        } else {
          zs = tH / ts;
          width = ar * ts;
        }
      } else {
        width = ts;
        zs = tH / ts;
      }
    } else if (imageProps) {
      const ar = imageProps.width / imageProps.height;
      width = ar * ts;
      zs = tH / ts;
    } else {
      width = item.cache.width;
      zs = tH / item.cache.height;
    }
    const asc = dpi / zs; // base annotation scale for drawing
    const w = Math.ceil(width * zs) + dpi * (left + right); // width of drawing in physical px
    const qtw = $settingDisplayTnQty ? qtyTextWidth(ctx, 12, asc) * zs : 0;
    const mw = $settingDisplayTnMark ? markWidth(ctx, 12, asc) * zs : 0; // get width of mark label
    const ox = item.quantity !== 1 && qtw > w ? Math.floor(qtw) : 0; // width of quantity label
    const aw = Math.max(w, ox, mw); // Max of item, qty, mark, dim
    const adjustedW = Math.min(aw, Math.ceil(tH * maxAspectRatio));
    const cw = Math.ceil(adjustedW / dpi); // width of drawing in css px
    const xs = (adjustedW - w) / 2;

    return {
      h,
      zs,
      asc,
      w,
      tH,
      qtw,
      mw,
      adjustedW,
      cw,
      xs,
    };
  }

  function getOffsetY(ctx, thumbnailHt) {
    if (!ctx) return null;
    return thumbnailHt < 20 ? thumbnailHt + 15 : null;
  }

  function getOffsetX(ctx, offsetY, renderProps) {
    if (!ctx) return 0;
    if (offsetY) return 0;

    return renderProps.qtw > renderProps.w ? renderProps.qtw / renderProps.zs : 0;
  }

  function getContainerHt(i, c, oy, sds) {
    let ht;
    if (replaceWithImage) {
      if (sds) {
        ht = sds.height;
      } else {
        ht = thumbnailHt;
      }
    } else {
      ht = i.cache.height;
    }
    const pct = ht / maxHeight;
    return Math.max(maxH * pct, 30) + top + bottom + (oy ? 15 : 0);
  }

  function getScaledDrawingSize(item) {
    if (!item.drawing?.clip?.drawing_scale) return null;
    const m = multiplier(item.drawing.clip.drawing_scale);
    const width = (item.drawing.properties.width * m) / 300;
    const height = (item.drawing.properties.height * m) / 300;
    return { width, height };
  }

  function getThumbnailHt(item, cssHeight, maxHeight, rwi, ip, sds) {
    let ht;

    if ((item.drawing?.properties && item.replace_with_drawing) || rwi) {
      if (sds) {
        ht = sds.height + 4;
      } else {
        ht = ts;
      }
    } else {
      ht = item.cache.height;
    }

    const pct = ht / maxHeight;
    return Math.floor(maxH * pct);
  }

  function getThumbnailWd(th, ox, mh, maxAspectRatio, item, rwi, ip, sds) {
    let pct;

    if (item.drawing && item.replace_with_drawing) {
      if (item.drawing?.properties) {
        if (sds) {
          pct = Math.min(sds.width / sds.height, maxAspectRatio);
        } else {
          pct = Math.min(item.drawing.properties.width / item.drawing.properties.height, maxAspectRatio);
        }
      } else {
        pct = 1;
      }
    } else if (rwi) {
      if (ip) {
        pct = Math.min(ip.width / ip.height, maxAspectRatio);
      } else {
        pct = 1;
      }
    } else {
      pct = Math.min(item.cache.width / item.cache.height, maxAspectRatio);
    }

    return Math.floor(th * pct) + 1;
  }

  function getLeftOffset(rp, tw, offsetX) {
    if (!rp) return 0;
    if (offsetX && item.quantity !== 1) {
      const dist = offsetX / 2;
      const scale = rp.zs / dpi;
      return dist * scale;
    }
    return 0;
  }

  function qtyTextWidth(ctx, fontSize, annoScale) {
    const txt = `(${item.quantity})`;
    ctx.font = `${fontSize * annoScale}px Menlo`;
    const m = ctx.measureText(txt);
    return m.width;
  }

  function markWidth(ctx, fontSize, annoScale) {
    ctx.font = `${fontSize * annoScale}px Menlo`;
    const min = 2 * fontSize * annoScale;
    const labelBy = isCollection ? $group.data.settings.collections_label_by : $group.data.settings.label_by;
    const m = ctx.measureText(get(item, labelBy) || "");
    const tw = m.width;
    return min + (tw - fontSize * annoScale) + 3;
  }

  function render(renderProps) {
    const { h, zs, asc, adjustedW, cw, xs } = renderProps;

    canvas.style.width = `${cw}px`;
    canvas.style.height = `${containerHt}px`;
    canvas.width = adjustedW;
    canvas.height = h;

    // zoom
    ctx.transform(zs, 0, 0, zs, 0, 0);

    // translate
    const dx = (left * dpi + xs) / zs;
    const dy = (h - bottom * dpi) / zs;
    ctx.transform(1, 0, 0, 1, dx, dy);

    drawing.render({
      ctx,
      annoScale: asc,
    });
  }

  function selectItem(evt) {
    $selectedType = null;

    if (evt.shiftKey && !isEmpty($selectionSet)) {
      const allIds = itemList.map((i) => i.id);
      const indices = itemList
        .map((i, index) => index)
        .filter((index) => $selectionSet[itemList[index].id] || itemList[index].id === item.id);

      const min = Math.min(...indices);
      const max = Math.max(...indices);

      const ids = allIds.slice(min, max + 1);
      selectionSet.selectOnly(...ids);
    } else if (discontiguousKey(evt)) {
      if (!$selectionSet[item.id]) {
        selectionSet.select(item.id);
      } else {
        selectionSet.deselect(item.id);
      }
    } else {
      selectionSet.selectOnly(item.id);
    }
  }

  function dragstart(e) {
    const { event, x, y } = e.detail;
    event.stopPropagation();

    if (modifierKey(event)) {
      selectItem(event);
      return;
    }

    dragStartPt = { x, y };
    if ($selectionSet[item.id]) {
      dragging = { items: $selectionSet, category };
    } else {
      dragging = { items: { [item.id]: true }, category };
    }
  }

  function drag(e) {
    const p = e.detail;

    const d = dragStartPt ? distSq(p, dragStartPt) : 0;

    if (dragging && d > 100) {
      isDragging = true;
    } else {
      isDragging = false;
    }
  }

  function dragend(e) {
    const { event } = e.detail;
    if (!modifierKey(event)) {
      selectItem(event);
    }

    isDragging = false;
    dragging = null;
  }

  function nav() {
    const newpath = `${$pathRoot}${isCollection ? "openings" : "items"}/${item.id}`;
    navigate(newpath);
  }

  onMount(() => {
    ctx = canvas.getContext("2d");
  });
</script>

<button
  class="flex-none cursor-pointer relative text-center flex flex-col items-center"
  style={`height:${containerHt}px;z-index: 0;`}
  use:draggable
  on:dragstart={dragstart}
  on:drag={drag}
  on:dragend={dragend}
  on:dblclick={nav}
  bind:this={container}
  on:click|stopPropagation>
  <a class="anchor" id={`thumbnail-${item.id}`} />
  <div
    class="absolute w-full"
    style="
      left:{leftOffset}px;
      bottom:{bottom - 1}px;
    ">
    {#if $settingDisplayTnBadges}
      <ItemBadges {comments} {attachments} items={collectionItems} quantity={item.quantity} />
    {/if}
    <div
      class="mx-auto mt-1"
      class:dragging={isDragging && dragging.items[item.id]}
      class:ring-4={selected}
      style="
        height:{thumbnailHt + 2}px;
        width:{thumbnailWd ? thumbnailWd + 'px' : '100%'};
      ">
      &nbsp;
    </div>
  </div>
  {#if replaceWithImage}
    <div
      class="absolute w-full"
      style="
          left:{leftOffset}px;
          bottom:{bottom - 1}px;
          z-index: -1;
        ">
      <div
        class="mx-auto"
        style="
        height:{thumbnailHt + 2}px;
        width:{thumbnailWd}px;
      ">
        <img
          class="w-full h-full border border-gray-400 object-contain text-xs text-gray-400"
          src={drawingSrc}
          alt="Item Thumbnail" />
      </div>
    </div>
  {/if}
  <div class="h-full contents" class:dragging={isDragging && dragging.items[item.id]} style="z-index: 1;">
    <canvas bind:this={canvas} />
  </div>
  {#if $settingDisplayTnDims && (!!(item.width && item.height) || item.shape.type === "none")}
    <div class="dimension-text" class:shift-bottom={$settingDisplayTnMark}>{dimstring}</div>
  {/if}
</button>

<style lang="scss">
  .anchor {
    display: block;
    position: relative;
    top: -4rem;
    visibility: hidden;
  }

  .dragging {
    @apply border border-dashed bg-gray-200 border-gray-400;
  }
  .dragging.contents {
    visibility: hidden;
  }

  .dimension-text {
    @apply relative text-center text-xs whitespace-nowrap;
    margin-bottom: -3rem;
    margin-top: -1rem;

    &.shift-bottom {
      margin-top: -3rem;
    }
  }
</style>
